import { BooleanInput } from '@angular/cdk/coercion';
import { NgClass, NgIf, NgFor } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ProfileService } from 'app/core/profile/profile.service';
import { AccountWithSubAccounts, ProfileWithNames } from 'app/core/profile/profile.types';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'user',
  standalone: true,
  imports: [
    MatButtonModule,
    MatMenuModule,
    NgIf,
    NgFor,
    NgClass,
    MatIconModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    TranslateModule,
  ],
})
export class UserComponent implements OnInit, OnDestroy {
  static ngAcceptInputType_showAvatar: BooleanInput;

  @Input() showAvatar: boolean = true;

  user: User;
  profilesGrouped: AccountWithSubAccounts[] = [];
  expandedAccounts: Set<string> = new Set();
  isLoading = false;
  activeProfileId: string | null = null;

  private readonly _changeDetectorRef = inject(ChangeDetectorRef);
  private readonly _router = inject(Router);
  private readonly _userService = inject(UserService);
  private readonly _profileService = inject(ProfileService);
  private _unsubscribeAll: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this._userService.user$.pipe(takeUntil(this._unsubscribeAll)).subscribe((user: User) => {
      this.user = user;
      this._changeDetectorRef.markForCheck();
    });

    this._profileService.profilesGrouped$.pipe(takeUntil(this._unsubscribeAll)).subscribe((grouped) => {
      this.profilesGrouped = grouped;
      this._changeDetectorRef.markForCheck();
    });

    this._profileService.activeProfileId$.pipe(takeUntil(this._unsubscribeAll)).subscribe((profileId) => {
      this.activeProfileId = profileId;
      this._changeDetectorRef.markForCheck();
    });

    this._profileService.isLoading$.pipe(takeUntil(this._unsubscribeAll)).subscribe((loading) => {
      this.isLoading = loading;
      this._changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  onMenuOpened(): void {
    if (this.profilesGrouped.length === 0) {
      this._profileService.loadProfilesGrouped().pipe(takeUntil(this._unsubscribeAll)).subscribe();
    }
  }

  toggleAccount(accountUID: string, event: Event): void {
    event.stopPropagation();
    if (this.expandedAccounts.has(accountUID)) {
      this.expandedAccounts.delete(accountUID);
    } else {
      this.expandedAccounts.add(accountUID);
    }
    this._changeDetectorRef.markForCheck();
  }

  isAccountExpanded(accountUID: string): boolean {
    return this.expandedAccounts.has(accountUID);
  }

  selectProfile(profile: ProfileWithNames, event: Event): void {
    event.stopPropagation();

    if (profile.isActive) return;

    this._profileService
      .switchProfile(profile.profileId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: () => {
          window.location.reload();
        },
        error: (err) => {
          console.error('Error switching profile:', err);
        },
      });
  }

  signOut(): void {
    this._router.navigate(['/sign-out']);
  }

  changePassword(): void {
    this._router.navigate(['/change-password']);
  }
}
